<template>
  <CommonProperty
    name="Container Type"
    :editable="canEdit ? selected : undefined"
    :options="canEdit ? options : undefined"
    :loading="isSaving"
    @onSave="handleSave"
  >
    {{ typeName }}
  </CommonProperty>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAbility } from '@casl/vue';
import { useToast } from 'vue-toastification';
import { getAccountContainerTypes, updateDevice } from '@gpx-spa/queries';
import CommonProperty from '@/components/_common/CommonProperty.vue';
import { COMMON_UPDATE_ERROR, COMMON_UPDATE_SUCCESS } from '@/config/constants';

// Non-numeric so it survives VSelect's coercion of the option value to a number.
const NONE = 'none';

const props = defineProps({
  device: {
    type: Object,
    default: null,
  },
  deviceId: {
    type: Number,
    required: true,
  },
});

const store = useStore();
const toast = useToast();
const { can } = useAbility();
const { mutate: mutateContainerType, isPending: isSaving } = updateDevice();

const accountId = computed(() => store.getters['auth/accountId']);
const canEdit = computed(() => can('update', 'Devices'));

// The whole vocabulary in one page: a dropdown cannot be paged, and the largest account has
// tens of types, not thousands.
const { data } = getAccountContainerTypes(accountId, { per_page: 1000 });

const types = computed(() => data.value?.data || []);
const typeName = computed(() => props.device?.container_type?.name || '-');
const selected = computed(() => props.device?.container_type_id ?? NONE);

const options = computed(() => [
  { value: NONE, label: 'None' },
  ...types.value.map((type) => ({ value: type.id, label: type.name })),
]);

const handleSave = (value) => {
  mutateContainerType(
    {
      deviceId: props.deviceId,
      payload: { container_type_id: value === NONE ? null : value },
    },
    {
      onSuccess: () => {
        toast.success(COMMON_UPDATE_SUCCESS);
      },
      onError: () => {
        toast.error(COMMON_UPDATE_ERROR);
      },
    },
  );
};
</script>
